import { useState, useEffect } from 'react'
import { useNavigate, useLocation } from 'react-router-dom'
import { Link } from 'react-router-dom'
import { Search, Menu } from 'lucide-react'
import SXTIcon from '@/components/icons/sxt-color.svg';
import SuiExplorerIcon from '@/components/icons/sui-explorer.svg';

import { api } from '@/lib/api'

import { Button } from '@/components/ui/button'
import { Input } from '@/components/ui/input'

import {
	Sheet,
	SheetContent,
	SheetHeader,
	SheetTitle,
	SheetTrigger,
} from "@/components/ui/sheet"

import {
	Dialog,
	DialogContent,
	DialogDescription,
	DialogHeader,
	DialogTitle,
	DialogTrigger,
} from "@/components/ui/dialog"


import { LeftNav } from './LeftNav';

export function Header() {
	const [query, setQuery] = useState('')
	const navigate = useNavigate()
	const location = useLocation()

	const handleSearch = async (e: React.FormEvent) => {
		e.preventDefault()
		if (!query.trim()) return

		const searchResult = await api.getSearchResponse(query);

		if (!searchResult.redirectId) {
			alert('no sui object found')
			return;
		}

		switch (searchResult.type) {
			case "checkpoint": navigate(`/checkpoints/${searchResult.redirectId}`); break;
			case "transaction": navigate(`/transaction-blocks/${searchResult.redirectId}`); break;
			case "account": navigate(`/accounts/${searchResult.redirectId}`); break;
			case "package": navigate(`/packages/${searchResult.redirectId}`); break;
			case "coin": navigate(`/coins/${searchResult.redirectId}`); break;
			case "object": navigate(`/objects/${searchResult.redirectId}`); break;
		}
		setSearchOpen(false);
	}

	// Helper function to get section from pathname
	const getSectionFromPath = (pathname: string) => {
		// Remove leading slash and get the first segment
		const path = pathname.replace('/', '');
		// If empty path, it's the overview section
		return path === '' ? 'overview' : path;
	};

	// Initialize selectedSection based on current URL
	const [selectedSection, setSelectedSection] = useState(() => getSectionFromPath(location.pathname));
	const [navOpen, setNavOpen] = useState<boolean>(false);
	const [searchOpen, setSearchOpen] = useState<boolean>(false);

	// Keep selectedSection in sync with URL changes
	useEffect(() => {
		const section = getSectionFromPath(location.pathname);
		setSelectedSection(section);
	}, [location.pathname]);

	const handleNavSelect = (id: string) => {
		setSelectedSection(id);

		// special consideration for 'overview' which should resolve to '/'
		navigate(id === "overview" ? '/' : `/${id}`);
		setNavOpen(false);
	};

	return (
		<header className="sticky top-0 z-50 w-full border-b backdrop-blur supports-[backdrop-filter]:bg-background/30">
			<div className="
				flex flex-row items-center
				px-4 py-2	
				
				">

				<div className='lg:hidden'>
					<Sheet open={navOpen} onOpenChange={setNavOpen}>
						<SheetTrigger asChild>
							<Menu className='h-8 w-8' />
						</SheetTrigger>
						<SheetContent side="left" className='bg-[#C0E6FF]/10 backdrop-blur-md p-0 w-60'>
							<SheetHeader className='p-2 m-0 border border-b-white/20'>
								<SheetTitle className='p-0 m-0'>
									<div className="flex items-center space-x-2">
										<img src={SuiExplorerIcon} className="w-5 h-5" alt="icon" />
										<span className="text-xl font-bold text-white">Sui Explorer</span>
									</div>
								</SheetTitle>
							</SheetHeader>
							<LeftNav
								selectedId={selectedSection}
								onSelect={handleNavSelect}
								className='backdrop-blur-none supports-[backdrop-filter]:bg-transparent w-auto'
							/>
						</SheetContent>
					</Sheet>
				</div>



				{/* Brand & Navigation */}
				<div className='flex-1 sm:flex-none flex flex-col gap-0 px-2 items-center'>
					<Link to="/" className="flex items-center space-x-2">
						<img src={SuiExplorerIcon} className="w-5 h-5" alt="icon" />
						<span className="text-xl font-bold text-white">Sui Explorer</span>
					</Link>
					<div className='flex flex-row gap-1 text-[0.5rem] items-center text-muted-foreground'>
						<span>Powered by Space and Time</span>
						<a href="https://www.spaceandtime.io/" target="_blank" rel="noopener noreferrer">
							<img src={SXTIcon} className="w-3 h-3" alt="icon" />
						</a>
					</div>
				</div>

				{/* Search */}
				<div className='hidden sm:block sm:flex-1 md:ml-20'>
					<div className="flex flex-1 w-full">
						<div className="relative w-full">
							<form onSubmit={handleSearch} className="flex w-full items-center space-x-2">
								<div className="relative flex-1">
									<Search className="absolute left-2.5 top-2.5 h-4 w-4 text-muted-foreground" />
									<Input
										type="search"
										placeholder="Search by checkpoint digest / transaction digest / account address / package ID / coin type / object ID"
										className="pl-8 bg-[#C0E6FF]/15 backdrop-blur-md border border-white/20 rounded-lg shadow-lg"
										value={query}
										onChange={(e) => setQuery(e.target.value)}
									/>
								</div>
								<Button type="submit" className="hidden sm:flex text-white bg-[#011829] border border-white/10">
									Search
								</Button>
							</form>
						</div>
					</div>
				</div>
				<div className='sm:hidden'>
					<Dialog open={searchOpen} onOpenChange={setSearchOpen}>
						<DialogTrigger>
							<Search className='h-6 w-6 ml-2' />
						</DialogTrigger>
						<DialogContent className='bg-transparent border border-none fixed left-1/2 top-16 -translate-x-1/2 w-[90vw] max-w-md'>
							<DialogHeader className='flex flex-row w-full p-2'>
								{/* <DialogTitle>Search</DialogTitle> */}
							</DialogHeader>
							<form onSubmit={handleSearch} className="flex w-full items-center space-x-2">
								<div className="relative flex-1">
									<Search className="absolute left-2.5 top-2.5 h-4 w-4 text-muted-foreground" />
									<Input
										type="search"
										placeholder="Search by checkpoint digest / transaction digest / account address / package ID / coin type / object ID"
										className="pl-8 bg-[#C0E6FF]/15 backdrop-blur-md border border-white/20 rounded-lg shadow-lg"
										value={query}
										onChange={(e) => setQuery(e.target.value)}
									/>
								</div>
								<Button type="submit" className="hidden sm:flex text-white bg-[#011829] border border-white/10">
									Search
								</Button>
							</form>
						</DialogContent>
					</Dialog>

				</div>



			</div>
		</header>
	)
}